import React, { useState } from 'react';
import CoreSkeletonBox from 'src/shared/CoreSkeletonBox';
import styled from 'styled-components';
import {
	firstSectionWrapperClass,
	updatedTextClass,
	updatedTextWrapperClass,
} from '@components/report/style.css';
import { useYtReportGet } from 'src/apis/orval/endpoints/v2-report/v2-report';
import { useSearchParams } from 'react-router-dom';
import { AudienceAdapter } from '@components/report/section-content/common/audience/AudienceAdapter';
import { useReportPollingContext } from '@components/report/report-layout/ReportPollingContext';
import { CoreButton, CoreSelect } from '@featuring-corp/components';
import { Trans, useTranslation } from 'react-i18next';
import ReportSection from '@components/report/report-section/ReportSection';
import Audience from '@components/report/section-content/common/audience/Audience';
import ReportSubSection from '@components/report/report-sub-section/ReportSubSection';
import ReportIndex from '@components/report/report-index/ReportIndex';
import useScrollIntoView from '@components/report/useScrollntoView';
import SectionMessages from '@components/report/section-messages/SectionMessages';
import { KeyMetricsYtAdapter } from '@components/report/section-content/yt/key-metrics/KeyMetricsYtAdapter';
import { FollowerGrowthYtAdapter } from '@components/report/section-content/yt/follower-growth/FollowerGrowthAdapter';
import { EngagementYtAdapter } from '@components/report/section-content/yt/engagement/EngagementYtAdapter';
import { ContentYtAdapter } from '@components/report/section-content/yt/content/ContentYtAdapter';
import { VideoContentYtAdapter } from '@components/report/section-content/yt/video-content/VideoContentYtAdapter';
import { ShortsContentAdapter } from '@components/report/section-content/yt/shorts-content/ShortsContentAdapter';
import { AdContentAdapter } from '@components/report/section-content/yt/ad-content/AdContentAdapter';
import { SimilarGroupYtAdapter } from '@components/report/section-content/yt/similar-group/SimilarGroupYtAdapter';
import KeyMetricsYt from '@components/report/section-content/yt/key-metrics/KeyMetrics';
import FollowerGrowthYt from '@components/report/section-content/yt/follower-growth/FollowerGrowth';
import EngagementYt from '@components/report/section-content/yt/engagement/EngagementYt';
import ContentYt from '@components/report/section-content/yt/content/ContentYt';
import VideoContentYt from '@components/report/section-content/yt/video-content/VideoContentYt';
import ShortsContent from '@components/report/section-content/yt/shorts-content/ShortsContent';
import AdContent from '@components/report/section-content/yt/ad-content/AdContent';
import SimilarGroupYt, {
	SimilarGroupYtProps,
} from '@components/report/section-content/yt/similar-group/SimilarGroupYt';

const indexItems = [
	'핵심 지표',
	'구독자 성장률',
	'오디언스',
	'인게이지먼트',
	'콘텐츠',
	'유사 그룹',
];

const YoutubeContainer = () => {
	const { t } = useTranslation('infReportYoutube');
	const [search_params] = useSearchParams();
	const platformPk = search_params.get('pk') as string;
	const {
		isReportLoading,
		isUpdateLoading,
		needUpdate,
		readyForUpdate,
		isPollingFailed,
	} = useReportPollingContext();

	const { selectedSection, moveScroll } = useScrollIntoView('h2');

	const [sortVideoContentBy, setSortVideoContentBy] = useState('newest');
	const [sortShortsContentBy, setSortShortsContentBy] = useState('newest');

	const {
		data: {
			keyMetrics,
			followerGrowth,
			audience,
			engagement,
			content,
			videoContent,
			shortsContent,
			adContent,
			similarGroup,
		} = {},
		isLoading: isQueryLoading,
		isError: isQueryError,
		error,
		refetch,
	} = useYtReportGet(platformPk, {
		query: {
			select: (data) => ({
				keyMetrics: new KeyMetricsYtAdapter(data).adapt(),
				followerGrowth: new FollowerGrowthYtAdapter(data).adapt(),
				audience: new AudienceAdapter(data).adapt(),
				engagement: new EngagementYtAdapter(data).adapt(),
				content: new ContentYtAdapter(data).adapt(),
				videoContent: new VideoContentYtAdapter(data).adapt(
					sortVideoContentBy as 'newest' | 'views',
				),
				shortsContent: new ShortsContentAdapter(data).adapt(
					sortShortsContentBy as 'newest' | 'views',
				),
				adContent: new AdContentAdapter(data).adapt(),
				similarGroup: new SimilarGroupYtAdapter(data).adapt(),
			}),
			refetchOnWindowFocus: false,
			enabled: !!platformPk,
			retry: false,
		},
	});

	const isLoading = isQueryLoading || isReportLoading;
	// polling 이 완료 없이 종료된 경우(에러/시간 초과)도 에러 화면과 동일하게 처리
	const isError = isQueryError || isPollingFailed;

	return (
		<>
			<ReportIndex
				indexItems={indexItems}
				selectedSection={selectedSection ?? ''}
				handleMoveScroll={moveScroll}
			/>
			<ReportContentWrapper>
				{/* 섹션 메시지 */}
				<SectionMessages
					isError={isError}
					errorCode={(error as any)?.statusCode}
					isLoading={isLoading}
				/>

				<div className={firstSectionWrapperClass}>
					<div className={updatedTextWrapperClass}>
						<p className={updatedTextClass}>
							Last update:{' '}
							{isLoading ? (
								<CoreSkeletonBox _width="80px" _height="14px" radius="4px" />
							) : (
								keyMetrics?.updatedAt ?? ' - '
							)}
						</p>
						{needUpdate && (
							<CoreButton
								text={t('report.youtube.yt-report.button.need-update')}
								buttonType="secondary"
								size="sm"
								isLoading={isUpdateLoading}
								disabled={!isUpdateLoading && !readyForUpdate}
								onClick={() => refetch()}
							/>
						)}
					</div>

					{/* 핵심 지표 */}
					<ReportSection
						title={t('report.youtube.yt-report.item.key-metrics')}
						tooltip={t('report.youtube.yt-report.report-section.key-metrics.tooltip')}
						description={t(
							'report.youtube.yt-report.report-section.key-metrics.description',
						)}
					>
						<KeyMetricsYt
							isLoading={isLoading}
							isError={isError}
							{...keyMetrics?.props}
						/>
					</ReportSection>
				</div>

				{/* 구독자 성장률 */}
				<ReportSection
					title={t('report.youtube.yt-report.item.follower-growth')}
					description={t(
						'report.youtube.yt-report.report-section.follower-growth-yt.description',
					)}
				>
					<FollowerGrowthYt isLoading={isLoading} isError={isError} {...followerGrowth} />
				</ReportSection>

				{/* 오디언스 */}
				<ReportSection
					title={t('report.youtube.yt-report.item.audience')}
					description={t('report.youtube.yt-report.report-section.audience.description')}
				>
					<Audience
						platformCode="yt"
						{...audience}
						isLoading={isLoading}
						isError={isError}
					/>
				</ReportSection>

				<ReportSection
					title={t('report.youtube.yt-report.item.engagement')}
					description={t(
						'report.youtube.yt-report.report-section.engagement.description',
					)}
				>
					<EngagementYt isLoading={isLoading} isError={isError} {...engagement} />
				</ReportSection>

				{/* 콘텐츠 */}
				<ReportSection title={t('report.youtube.yt-report.item.content')}>
					<ContentYt {...content} isLoading={isLoading} isError={isError} />
				</ReportSection>

				{/* 동영상 콘텐츠 */}
				<ReportSubSection
					title={t('report.youtube.yt-report.report-subsection.video-content-yt.title')}
					tooltip={t(
						'report.youtube.yt-report.report-subsection.video-content-yt.title-content.tooltip',
					)}
					titleContent={
						<CoreSelect
							size="sm"
							width={171}
							defaultValue={sortVideoContentBy}
							setValue={setSortVideoContentBy}
						>
							<CoreSelect.Item value="newest">{t('sort-content.newest')}</CoreSelect.Item>
							<CoreSelect.Item value="views">{t('sort-content.views')}</CoreSelect.Item>
						</CoreSelect>
					}
				>
					<VideoContentYt {...videoContent} isLoading={isLoading} isError={isError} />
				</ReportSubSection>

				{/* 쇼츠 콘텐츠 */}
				<ReportSubSection
					title={t('report.youtube.yt-report.report-subsection.shorts-content.title')}
					tooltip={t(
						'report.youtube.yt-report.report-subsection.shorts-content.title-content.tooltip',
					)}
					titleContent={
						<CoreSelect
							size="sm"
							width={171}
							defaultValue={sortShortsContentBy}
							setValue={setSortShortsContentBy}
						>
							<CoreSelect.Item value="newest">{t('sort-content.newest')}</CoreSelect.Item>
							<CoreSelect.Item value="views">{t('sort-content.views')}</CoreSelect.Item>
						</CoreSelect>
					}
				>
					<ShortsContent {...shortsContent} isLoading={isLoading} isError={isError} />
				</ReportSubSection>

				{/* 유료 광고 콘텐츠 */}
				<ReportSubSection
					title={t('report.youtube.yt-report.report-subsection.ad-content.title')}
				>
					<AdContent {...adContent} isLoading={isLoading} />
				</ReportSubSection>

				{/* 유사 그룹 */}
				<ReportSection
					title={t('report.youtube.yt-report.item.similar-group')}
					tooltip={t('report.youtube.yt-report.report-section.similar-group-yt.tooltip')}
					description={
						<>
							<Trans
								t={t}
								i18nKey="report.youtube.yt-report.report-section.similar-group-yt.description"
								components={[
									<span key={'user-name'}>{similarGroup?.desc?.userName ?? '-'}</span>,
									<span key={'category'}>{similarGroup?.desc?.category ?? '-'}</span>,
									<span key={'followers'}>
										{t(
											'report.youtube.yt-report.report-section.similar-group-yt.followers',
											{
												followers: similarGroup?.desc?.followers ?? '-',
											},
										)}
									</span>,
								]}
							/>
						</>
					}
				>
					<SimilarGroupYt
						{...(similarGroup?.props as SimilarGroupYtProps<string[]>)}
						isLoading={isLoading}
						isError={isError}
					/>
				</ReportSection>
			</ReportContentWrapper>
		</>
	);
};

const ReportContentWrapper = styled.div`
	overflow: hidden;
	height: auto;
	min-height: 100%;
	flex-grow: 1;
	flex-shrink: 0;
	padding: 60px 0 150px;

	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: center;

	gap: 80px;
`;

export default YoutubeContainer;
